import { FeatureErrorBoundary } from "@components/FeatureErrorBoundary";
import { RouteErrorComponent } from "@components/RouteErrorComponent";
import { SuspenseLoader } from "@components/SuspenseLoader";
import { authKeys } from "@features/auth";
import { authApi } from "@features/auth";
import { AppLayout } from "@layouts/AppLayout";
import { SettingsLayout } from "@layouts/SettingsLayout";
import { queryClient } from "@lib/queryClient";
import { useAuthStore } from "@stores/authStore";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { type ReactNode, Suspense, lazy } from "react";

// Lazy-loaded pages
const LandingPage = lazy(() =>
  import("@pages/landing/LandingPage").then((m) => ({
    default: m.LandingPage,
  })),
);
const LoginPage = lazy(() =>
  import("@pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ChatPage = lazy(() =>
  import("@pages/chat/ChatPage").then((m) => ({ default: m.ChatPage })),
);
const ProjectsPage = lazy(() =>
  import("@pages/projects/ProjectsPage").then((m) => ({
    default: m.ProjectsPage,
  })),
);
const ProjectLayoutPage = lazy(() =>
  import("@pages/projects/detail/ProjectLayoutPage").then((m) => ({
    default: m.ProjectLayoutPage,
  })),
);
const ProjectOverviewPage = lazy(() =>
  import("@pages/projects/detail/ProjectOverviewPage").then((m) => ({
    default: m.ProjectOverviewPage,
  })),
);
const ProjectWorkbenchPage = lazy(() =>
  import("@pages/projects/detail/ProjectWorkbenchPage").then((m) => ({
    default: m.ProjectWorkbenchPage,
  })),
);
const BoardOverviewPage = lazy(() =>
  import("@pages/projects/detail/BoardOverviewPage").then((m) => ({
    default: m.BoardOverviewPage,
  })),
);
const ProjectBomPage = lazy(() =>
  import("@pages/projects/detail/ProjectBomPage").then((m) => ({
    default: m.ProjectBomPage,
  })),
);
const ProjectPcbPage = lazy(() =>
  import("@pages/projects/detail/ProjectPcbPage").then((m) => ({
    default: m.ProjectPcbPage,
  })),
);
const ProjectVersionsPage = lazy(() =>
  import("@pages/projects/detail/ProjectVersionsPage").then((m) => ({
    default: m.ProjectVersionsPage,
  })),
);
const ProjectChatPage = lazy(() =>
  import("@pages/projects/detail/ProjectChatPage").then((m) => ({
    default: m.ProjectChatPage,
  })),
);
const ProjectCircuitsPage = lazy(() =>
  import("@pages/projects/detail/ProjectCircuitsPage").then((m) => ({
    default: m.ProjectCircuitsPage,
  })),
);
const ProjectBriefPage = lazy(() =>
  import("@pages/projects/detail/ProjectBriefPage").then((m) => ({
    default: m.ProjectBriefPage,
  })),
);
const ProjectCablesPage = lazy(() =>
  import("@pages/projects/detail/ProjectCablesPage").then((m) => ({
    default: m.ProjectCablesPage,
  })),
);
const ProjectSpecsPage = lazy(() =>
  import("@pages/projects/detail/ProjectSpecsPage").then((m) => ({
    default: m.ProjectSpecsPage,
  })),
);
const ProjectRequirementsPage = lazy(() =>
  import("@pages/projects/detail/ProjectRequirementsPage").then((m) => ({
    default: m.ProjectRequirementsPage,
  })),
);
const ProjectSyncPage = lazy(() =>
  import("@pages/projects/detail/ProjectSyncPage").then((m) => ({
    default: m.ProjectSyncPage,
  })),
);
const ProjectConnectorsPage = lazy(() =>
  import("@pages/projects/detail/ProjectConnectorsPage").then((m) => ({
    default: m.ProjectConnectorsPage,
  })),
);
const ProjectSchematicPage = lazy(() =>
  import("@pages/projects/detail/ProjectSchematicPage").then((m) => ({
    default: m.ProjectSchematicPage,
  })),
);
const SnapshotReviewPage = lazy(() =>
  import("@pages/projects/detail/SnapshotReviewPage").then((m) => ({
    default: m.SnapshotReviewPage,
  })),
);
const HistoryPage = lazy(() =>
  import("@pages/history/HistoryPage").then((m) => ({
    default: m.HistoryPage,
  })),
);
const OrganisationsListPage = lazy(() =>
  import("@pages/settings/OrganisationsListPage").then((m) => ({
    default: m.OrganisationsListPage,
  })),
);
const OrganisationDetailPage = lazy(() =>
  import("@pages/settings/OrganisationDetailPage").then((m) => ({
    default: m.OrganisationDetailPage,
  })),
);
const JoinInvitePage = lazy(() =>
  import("@pages/join/JoinInvitePage").then((m) => ({
    default: m.JoinInvitePage,
  })),
);
const SharedChatPage = lazy(() =>
  import("@pages/share/SharedChatPage").then((m) => ({
    default: m.SharedChatPage,
  })),
);
const NotFoundPage = lazy(() =>
  import("@pages/fallbacks/NotFoundPage").then((m) => ({
    default: m.NotFoundPage,
  })),
);
const BillingPage = lazy(() =>
  import("@pages/settings/BillingPage").then((m) => ({
    default: m.BillingPage,
  })),
);
const ApiKeysPage = lazy(() =>
  import("@pages/settings/ApiKeysPage").then((m) => ({
    default: m.ApiKeysPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@pages/settings/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const PricingPage = lazy(() =>
  import("@pages/pricing/PricingPage").then((m) => ({
    default: m.PricingPage,
  })),
);
const TermsPage = lazy(() =>
  import("@pages/legal/LegalPages").then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import("@pages/legal/LegalPages").then((m) => ({ default: m.PrivacyPage })),
);
const ContactPage = lazy(() =>
  import("@pages/legal/LegalPages").then((m) => ({ default: m.ContactPage })),
);
const AdminLayout = lazy(() =>
  import("@pages/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })),
);
const AdminUsersPage = lazy(() =>
  import("@pages/admin/AdminUsersPage").then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const AdminOrgsPage = lazy(() =>
  import("@pages/admin/AdminOrgsPage").then((m) => ({
    default: m.AdminOrgsPage,
  })),
);
const AdminBillingPage = lazy(() =>
  import("@pages/admin/AdminBillingPage").then((m) => ({
    default: m.AdminBillingPage,
  })),
);
const AdminAuditPage = lazy(() =>
  import("@pages/admin/AdminAuditPage").then((m) => ({
    default: m.AdminAuditPage,
  })),
);

function featurePage(name: string, node: ReactNode) {
  return (
    <FeatureErrorBoundary name={name}>
      <Suspense fallback={<SuspenseLoader />}>{node}</Suspense>
    </FeatureErrorBoundary>
  );
}

function publicPage(node: ReactNode) {
  return <Suspense fallback={<SuspenseLoader />}>{node}</Suspense>;
}

// Root route
const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={<SuspenseLoader />}>
      <Outlet />
    </Suspense>
  ),
  // Catch-all for unhandled errors anywhere in the route tree —
  // failed loaders, beforeLoad throws, render errors that bubble past
  // a child route's own `errorComponent`. Without this, an unhandled
  // loader error would crash the whole shell. `redirect` throws (used
  // in beforeLoad for legacy paths and the auth gate) are NOT caught
  // here; the router special-cases them.
  errorComponent: RouteErrorComponent,
});

// Public marketing routes
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => publicPage(<LandingPage />),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => publicPage(<LoginPage />),
});

// Legacy redirect — old /auth/login URL still works
const legacyAuthLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/login",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

/**
 * Auth gate shared by every protected shell. Inlined into each shell's
 * `beforeLoad` rather than hoisted into a parent id-only route — using
 * a parent route would change every child's composed id (`/app/chat`
 * → `/protected/app-shell/chat`), breaking all the typed `from:`
 * references in the codebase. Two short duplicated gates is a smaller
 * footprint than chasing every router-typed call site.
 */
async function authGate(): Promise<void> {
  if (useAuthStore.getState().user !== null) return;
  try {
    await queryClient.fetchQuery({
      queryKey: authKeys.me,
      queryFn: authApi.getMe,
      staleTime: 1000 * 60 * 5,
    });
  } catch (err) {
    // Only redirect to /login on an authentication failure. Network errors
    // and 5xxs shouldn't dump a signed-in user at the login screen — they
    // should surface through the in-page error UI instead. We treat 401/403
    // as "no longer authenticated"; anything else is fed back to the route
    // loader so the shell renders an error boundary.
    const status =
      (err as { status?: number; response?: { status?: number } })?.response
        ?.status ?? (err as { status?: number })?.status;
    if (status === 401 || status === 403) {
      throw redirect({ to: "/login" });
    }
    throw err;
  }
}

// Protected app route — no path segment, app routes keep their existing URLs.
// Auth gating happens here in `beforeLoad` so an unauthenticated visit
// redirects BEFORE the protected layout mounts — eliminates the
// SuspenseLoader flash that the old `<AuthGuard>` wrapper produced on every
// protected nav. The persisted authStore is the optimistic happy path
// (instant for repeat visits); when it's empty we fall through to
// `/auth/me` and decide based on the result. The query stays alive in the
// background via `useCurrentUser()` mounted in `AppLayout`, so a
// server-side session expiry surfaces through the axios 401 interceptor.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: authGate,
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
  // Localised so a `/auth/me` failure during the protected gate (or a
  // loader throw inside the app subtree) renders inside the app
  // chrome rather than blanking the whole page.
  errorComponent: RouteErrorComponent,
});

// Settings sub-shell — id-only parent route under `appRoute` that wraps
// every `/settings/*` page in a sub-nav rail. Lives inside `AppLayout`,
// so the global Sidebar and Header stay visible: leaving settings is
// the same gesture as leaving any other page. Replaces an earlier
// full-viewport overlay shell whose "Back to workspace" button walked
// history one step at a time and trapped users in `/settings/*`.
const settingsLayoutRoute = createRoute({
  getParentRoute: () => appRoute,
  id: "settings-layout",
  component: SettingsLayout,
});

// Chat supports deep-linking via `?s=<sessionId>` so a browser refresh
// lands back on the same conversation instead of the welcome screen. Using
// a search param (rather than a path segment like `/chat/$sessionId`)
// keeps the route matching identity stable — `ChatPage` is not remounted
// when the id appears or changes, which avoids tearing down in-flight
// streams and local scroll/edit state.
interface ChatSearch {
  s?: string;
}

function validateChatSearch(search: Record<string, unknown>): ChatSearch {
  const raw = search.s;
  return typeof raw === "string" && raw.length > 0 ? { s: raw } : {};
}

const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat",
  validateSearch: validateChatSearch,
  component: () => featurePage("Chat", <ChatPage />),
});

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects",
  component: () => featurePage("Projects", <ProjectsPage />),
});

// Parent route for the project workspace. Renders the project header
// (back, title, badges, prefetch) and an Outlet — child routes render
// the active surface (overview / BOM / graph / versions / chat). The
// layout also mirrors the URL `projectId` into `useProjectStore` so
// chat code that reads `activeProject` from the store keeps working.
const projectShellRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$projectId",
  component: () => featurePage("Project", <ProjectLayoutPage />),
});

// `/projects/$projectId` with no surface segment — bounce to Overview.
// `replace: true` keeps the bare URL out of history, so Back goes to
// /projects rather than yo-yo'ing through the redirect.
const projectShellIndexRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/projects/$projectId/overview",
      params,
      replace: true,
    });
  },
});

const projectOverviewRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/overview",
  component: () => featurePage("Project overview", <ProjectOverviewPage />),
});

// Workbench — the canonical home for AI-generated artefacts (analyses,
// simulations, comparisons, draft mutations). Project-scoped; artefacts
// carry their own scope tag. The tab strip mounts artefact tabs above
// the grid; `?tab=<artefactId>` survives refresh and deep-links to a
// specific tab (`?tab=index` or omitted = the all-artefacts grid).
// Open-tab list (per project) is persisted to localStorage by
// `useWorkbenchTabs`; this validator only carries the active tab.
interface WorkbenchSearch {
  tab?: string;
  // Active chat session id — the CopilotRail and the standalone /chat
  // route share the same `chatStore` singleton, so the rail's session
  // also needs to roundtrip through the URL. Sending a first message in
  // the rail writes `?s=<sid>` here so a refresh lands back on the same
  // conversation instead of bouncing to a fresh one. Same key as
  // `/chat?s=` for symmetry.
  s?: string;
}

function validateWorkbenchSearch(
  search: Record<string, unknown>,
): WorkbenchSearch {
  const out: WorkbenchSearch = {};
  const tab = search.tab;
  if (typeof tab === "string" && tab.length > 0) {
    // Stale ids (deleted artefact, wrong project) fall through to "index"
    // at the hook level — we only filter empty/non-string here.
    out.tab = tab;
  }
  const s = search.s;
  if (typeof s === "string" && s.length > 0) out.s = s;
  return out;
}

const projectWorkbenchRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/workbench",
  validateSearch: validateWorkbenchSearch,
  component: () => featurePage("Project workbench", <ProjectWorkbenchPage />),
});

// BOM surface — table-level filter / sort / selection state lives in the
// URL so a "show me ingest faults sorted by quantity desc" view is
// shareable. Arrays are encoded as comma-separated strings (`sel=R1,R2`)
// because TanStack Router doesn't roundtrip true arrays through the
// URL cleanly. Default values are omitted from the search object so the
// address bar stays clean when nothing is filtered.
//
// Keys (intentionally short to keep the URL readable):
//   q       text filter (case-insensitive substring match)
//   ds      datasheet-status filter, comma-separated subset of
//           ready|extracting|missing|failed|no_mpn (omit ⇒ no filter)
//   ft      fault filter — "fault" only (omit ⇒ "all")
//   sort    sort key — ref|value|mpn|datasheet|pin_count (omit ⇒ "ref")
//   order   sort order — asc|desc (omit ⇒ "asc")
//   sel     comma-separated selected refdes set (omit ⇒ none)
type BomSortKey = "ref" | "value" | "mpn" | "datasheet" | "pin_count";
type BomSortOrder = "asc" | "desc";
type BomDatasheetStatus =
  | "ready"
  | "extracting"
  | "missing"
  | "failed"
  | "no_mpn";

interface BomSearch {
  q?: string;
  ds?: string;
  ft?: "fault";
  sort?: BomSortKey;
  order?: BomSortOrder;
  sel?: string;
}

const BOM_SORT_KEYS: ReadonlySet<BomSortKey> = new Set([
  "ref",
  "value",
  "mpn",
  "datasheet",
  "pin_count",
]);
const BOM_DS_STATUSES: ReadonlySet<BomDatasheetStatus> = new Set([
  "ready",
  "extracting",
  "missing",
  "failed",
  "no_mpn",
]);

function validateBomSearch(search: Record<string, unknown>): BomSearch {
  const out: BomSearch = {};

  const q = search.q;
  if (typeof q === "string" && q.length > 0) out.q = q;

  const ds = search.ds;
  if (typeof ds === "string" && ds.length > 0) {
    // Drop unknown statuses silently — a stale link with a renamed status
    // shouldn't break the page, it should just ignore the bad token.
    const tokens = ds
      .split(",")
      .map((t) => t.trim())
      .filter((t): t is BomDatasheetStatus =>
        BOM_DS_STATUSES.has(t as BomDatasheetStatus),
      );
    if (tokens.length > 0) out.ds = tokens.join(",");
  }

  if (search.ft === "fault") out.ft = "fault";

  const sort = search.sort;
  if (typeof sort === "string" && BOM_SORT_KEYS.has(sort as BomSortKey)) {
    if (sort !== "ref") out.sort = sort as BomSortKey;
  }

  const order = search.order;
  if (order === "desc") out.order = "desc";

  const sel = search.sel;
  if (typeof sel === "string" && sel.length > 0) {
    // Refdes are short alphanumerics; trim/dedupe but don't otherwise
    // validate — the BOM page filters non-existent refs at render time.
    const refs = Array.from(
      new Set(
        sel
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      ),
    );
    if (refs.length > 0) out.sel = refs.join(",");
  }

  return out;
}

const projectBomRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/bom",
  validateSearch: validateBomSearch,
  component: () => featurePage("Project BOM", <ProjectBomPage />),
});

// ── Schematic search params ─────────────────────────────────────────────────
//
// `?view=blocks` switches the schematic surface from the dense
// IEEE-symbol diagram to the functional-blocks (Louvain) topology
// view. Default (param absent or any other value) renders the
// existing schematic.
//
// `?block=<id>` halos the listed block's components on the schematic
// view — set when the user clicks a block tile in Blocks mode and
// the page navigates them across to the dense schematic with the
// matching components ringed in phosphor cyan. Format mirrors the
// blocks-payload id ("block_1", "block_2", ...). Without `view`
// pinned, `block` only takes effect on the schematic side; toggling
// back to Blocks clears the halo (the page strips the param when
// switching views).
//
// Refresh-stable + shareable so an engineer can DM
// "/projects/.../schematic?view=schematic&block=block_3" and the
// recipient lands on the same focused view.
type SchematicView = "schematic" | "blocks";
interface SchematicSearch {
  view?: SchematicView;
  block?: string;
}
const SCHEMATIC_VIEWS: ReadonlySet<SchematicView> = new Set([
  "schematic",
  "blocks",
]);

// Block ids look like `block_<n>` — defensive cap so a stale link
// can't smuggle a 10kb string into the URL. Anything outside that
// shape gets dropped silently (the page falls back to "no halo").
const BLOCK_ID_PATTERN = /^block_\d{1,4}$/;

function validateSchematicSearch(
  search: Record<string, unknown>,
): SchematicSearch {
  const out: SchematicSearch = {};
  const view = search.view;
  if (typeof view === "string" && SCHEMATIC_VIEWS.has(view as SchematicView)) {
    // Drop the default ("schematic") so URLs stay clean — only the
    // non-default value ever appears in the bar.
    if (view !== "schematic") out.view = view as SchematicView;
  }
  const block = search.block;
  if (typeof block === "string" && BLOCK_ID_PATTERN.test(block)) {
    out.block = block;
  }
  return out;
}

// ── PCB search params ───────────────────────────────────────────────────────
//
// Canvas state that should survive a refresh / be deep-linkable —
// "send me this view" must work in chat. Pan/zoom is debounced into
// `?view` only after the viewport settles (500ms) so a casual drag
// doesn't flood the URL bar; everything else is committed eagerly
// because each value is a single discrete state change.
//
// Param shape:
//   `view`     — "x,y,w,h" mm (viewBox). Absent = fit-to-content.
//   `flip`     — "1" = mirrored bottom view. Absent = top view.
//   `sel`      — refdes (selected component). Future: comma list.
//   `hide`     — comma-separated layer keys that are hidden. Absent = all visible.
//   `drc`      — "1" = DRC overlay on.
//   `dsev`     — "error" | "warning" | "info" filter on the DRC overlay.
//   `ann`      — "key" | "all" | "full" — annotation density (off = absent).
//   `units`    — "in" — measurement units. Absent = mm.
//
// Validator drops malformed values silently — a renamed layer or
// stale param shouldn't break the page, just degrade to defaults.
type PcbDrcSeverity = "error" | "warning" | "info";
type PcbAnnotationTier = "key" | "all" | "full";
type PcbUnits = "in";
interface PcbSearch {
  view?: string;
  flip?: "1";
  sel?: string;
  hide?: string;
  drc?: "1";
  dsev?: PcbDrcSeverity;
  ann?: PcbAnnotationTier;
  units?: PcbUnits;
  colour?: "netclass";
}

const PCB_DRC_SEVERITIES: ReadonlySet<PcbDrcSeverity> = new Set([
  "error",
  "warning",
  "info",
]);
const PCB_ANNOTATION_TIERS: ReadonlySet<PcbAnnotationTier> = new Set([
  "key",
  "all",
  "full",
]);

// Defensive caps — a malformed link shouldn't smuggle a multi-MB
// blob into the URL. Layer keys are short tokens; selections are
// short alphanumerics.
const PCB_HIDE_MAX_TOKENS = 32;
const PCB_HIDE_TOKEN_MAX_LEN = 64;
const PCB_SEL_MAX_LEN = 32;

function validatePcbSearch(search: Record<string, unknown>): PcbSearch {
  const out: PcbSearch = {};

  // viewBox: "x,y,w,h" — four finite numbers, width/height > 0.
  const view = search.view;
  if (typeof view === "string") {
    const parts = view.split(",").map((s) => Number(s.trim()));
    if (
      parts.length === 4 &&
      parts.every((n) => Number.isFinite(n)) &&
      parts[2] > 0 &&
      parts[3] > 0
    ) {
      out.view = parts.join(",");
    }
  }

  if (search.flip === "1") out.flip = "1";

  const sel = search.sel;
  if (
    typeof sel === "string" &&
    sel.length > 0 &&
    sel.length <= PCB_SEL_MAX_LEN
  ) {
    out.sel = sel;
  }

  const hide = search.hide;
  if (typeof hide === "string" && hide.length > 0) {
    const tokens = hide
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0 && t.length <= PCB_HIDE_TOKEN_MAX_LEN)
      .slice(0, PCB_HIDE_MAX_TOKENS);
    if (tokens.length > 0) out.hide = tokens.join(",");
  }

  if (search.drc === "1") out.drc = "1";

  const dsev = search.dsev;
  if (
    typeof dsev === "string" &&
    PCB_DRC_SEVERITIES.has(dsev as PcbDrcSeverity)
  ) {
    out.dsev = dsev as PcbDrcSeverity;
  }

  const ann = search.ann;
  if (
    typeof ann === "string" &&
    PCB_ANNOTATION_TIERS.has(ann as PcbAnnotationTier)
  ) {
    out.ann = ann as PcbAnnotationTier;
  }

  if (search.units === "in") out.units = "in";

  if (search.colour === "netclass") out.colour = "netclass";

  return out;
}

// PCB layout surface — Phase 16. Shows the project's `.kicad_pcb` (or
// equivalent) as an SVG board view with footprint placements + tracks
// overlaid on a blueprint dot grid. 404s gracefully when the version
// has no PCB layer.
const projectPcbRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/pcb",
  validateSearch: validatePcbSearch,
  component: () => featurePage("Project PCB", <ProjectPcbPage />),
});

// Schematic view — sibling of PCB per `docs/project-board-model.md`
// §"Nav shape". Reserved slot today (V2 elkjs IR diagram renderer);
// the route exists so the BOARD section reads as complete.
const projectSchematicRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/schematic",
  validateSearch: validateSchematicSearch,
  component: () => featurePage("Project schematic", <ProjectSchematicPage />),
});

// ── Board-scoped routes ─────────────────────────────────────────────────────
//
// Per the per-board route refactor (docs/ux-redesign/05-roadmap.md):
// every per-board surface (Schematic / PCB / BOM / Connectors / Circuits)
// now has a board-scoped URL alongside the legacy flat one. The board
// id is part of the path so links are bookmarkable and shareable —
// engineers can DM "/projects/sentinel/boards/abc.../bom" and the
// recipient sees the right board, no hidden state.
//
// The board shell itself is a no-op outlet — its job is to declare the
// `$boardId` URL segment so children inherit it via useParams. The
// project shell (ProjectLayoutPage) reads the URL boardId and feeds it
// through ProjectShellContext, so per-board pages render the right
// board's data without further plumbing.
//
// Legacy flat routes (/pcb, /bom, etc.) keep working for one cycle —
// they fall through to ProjectLayoutPage's "store activeBoardId else
// boards[0]" resolution. A follow-up phase will redirect them.
const boardShellRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/boards/$boardId",
  component: () => <Outlet />,
});

// Board landing page — `/projects/$pid/boards/$bid` with no surface
// segment. Shows board name, description, counts + per-surface quick
// links. Reached when the user clicks a board name in the sidebar
// (the chevron alone toggles expansion; the name navigates here).
const boardShellIndexRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/",
  component: () => featurePage("Board overview", <BoardOverviewPage />),
});

const boardSchematicRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/schematic",
  validateSearch: validateSchematicSearch,
  component: () => featurePage("Board schematic", <ProjectSchematicPage />),
});

const boardPcbRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/pcb",
  validateSearch: validatePcbSearch,
  component: () => featurePage("Board PCB", <ProjectPcbPage />),
});

const boardBomRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/bom",
  validateSearch: validateBomSearch,
  component: () => featurePage("Board BOM", <ProjectBomPage />),
});

const boardConnectorsRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/connectors",
  component: () => featurePage("Board connectors", <ProjectConnectorsPage />),
});

const boardCircuitsRoute = createRoute({
  getParentRoute: () => boardShellRoute,
  path: "/circuits",
  component: () => featurePage("Board circuits", <ProjectCircuitsPage />),
});

const projectVersionsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/versions",
  component: () => featurePage("Project versions", <ProjectVersionsPage />),
});

// Project-scoped chat surface. Same `?s=<sessionId>` search-param
// validation as the standalone /chat route — keeps refresh / deep-link
// behaviour consistent across both entry points.
const projectChatRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/chat",
  validateSearch: validateChatSearch,
  component: () => featurePage("Project chat", <ProjectChatPage />),
});

// Snapshot review — landing target for the KiCad plugin's deep link
// after a push. URL: /projects/$projectId/snapshots/$snapshotId
const projectSnapshotReviewRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/snapshots/$snapshotId",
  component: () => featurePage("Snapshot review", <SnapshotReviewPage />),
});

// Multi-Circuit lifecycle surface — IR plan §8.3 + Phase 14. Lists every
// :Circuit under the project (main + candidates + fixtures + scratches)
// with kind/status badges and lifecycle gestures (branch, promote,
// release, archive, restore).
const projectCircuitsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/circuits",
  component: () => featurePage("Project circuits", <ProjectCircuitsPage />),
});

// ── Define cluster — Project-level surfaces holding the inputs the
// chat agent reads from (Brief / Specs / Cables).
// Per `docs/project-board-model.md`. The Brief surface owns what used
// to be on Overview's `description_md` editor; Cables replaces what
// was `/systems/$name`'s links editor; Specs is reserved (Phase 10+).
interface BriefSearch {
  edit?: boolean;
}

function validateBriefSearch(search: Record<string, unknown>): BriefSearch {
  // Truthy "1"/"true" → open the brief surface directly in edit mode.
  // Anything else collapses to read mode (default). Keeps the URL clean
  // when the flag isn't set so `?edit=0` doesn't pollute history.
  const raw = search.edit;
  const on = raw === true || raw === "1" || raw === "true";
  return on ? { edit: true } : {};
}

const projectBriefRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/brief",
  validateSearch: validateBriefSearch,
  component: () => featurePage("Project brief", <ProjectBriefPage />),
});

const projectSpecsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/specs",
  component: () => featurePage("Project specs", <ProjectSpecsPage />),
});

// Project · Requirements — categorised, tier-scoped requirements list
// with a dedicated assistant chat. Replaces the placeholder Specs slot
// while we figure out whether the two surfaces fold into one (likely)
// or stay distinct (Specs = derived/IR-shaped contract, Requirements
// = source-of-intent). Wired to a new route during the mock so both
// can coexist while the IA decision lands.
const projectRequirementsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/requirements",
  component: () =>
    featurePage("Project requirements", <ProjectRequirementsPage />),
});

const projectCablesRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/cables",
  component: () => featurePage("Project cables", <ProjectCablesPage />),
});

// Plugin sync — the project's first-class settings surface today.
// Houses plugin-sync IDs, status, and install paths. Future project-
// admin features (rename / share / archive / danger zone) can live
// alongside it on this page or get their own surface.
const projectSyncRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/sync",
  component: () => featurePage("Plugin sync", <ProjectSyncPage />),
});

// Per-board Connectors — the connector annotations editor lives here.
// Scoped to the active Board on multi-board projects (set by the
// sidebar picker); on single-board projects it operates on the only
// Board. Annotations feed the chat agent's first-turn context block.
const projectConnectorsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: "/connectors",
  component: () => featurePage("Connectors", <ProjectConnectorsPage />),
});

// Legacy `/systems/...` routes have been retired. A multi-board project
// is just a Project with N>1 Boards; there is no separate System
// container. Existing bookmarks redirect to `/projects` — the user can
// pick the wrapper Project (which now carries the multi-board metadata).
const legacySystemRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/systems/$systemName",
  beforeLoad: () => {
    throw redirect({ to: "/projects", replace: true });
  },
});

const legacySystemSubRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/systems/$systemName/$",
  beforeLoad: () => {
    throw redirect({ to: "/projects", replace: true });
  },
});

const historyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/history",
  component: () => featurePage("History", <HistoryPage />),
});

// `/settings` with no trailing path — bounce to the profile page, the
// natural landing target now that profile lives under settings.
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings",
  beforeLoad: () => {
    throw redirect({ to: "/settings/profile", replace: true });
  },
});

const profileRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/profile",
  component: () => featurePage("Profile", <ProfilePage />),
});

const organisationsListRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/organisations",
  component: () => featurePage("Organisations", <OrganisationsListPage />),
});

const organisationDetailRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/organisations/$orgId",
  component: () =>
    featurePage("Organisation detail", <OrganisationDetailPage />),
});

// Billing settings — scope follows the org-switcher just like usage
// summary did. `?upgrade=success` and `?topup=success` are the return
// targets for Stripe Checkout in production; the page reads them to
// show a one-shot success toast.
interface BillingSearch {
  upgrade?: "success";
  topup?: "success";
}

function validateBillingSearch(search: Record<string, unknown>): BillingSearch {
  const out: BillingSearch = {};
  if (search.upgrade === "success") out.upgrade = "success";
  if (search.topup === "success") out.topup = "success";
  return out;
}

const billingRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/billing",
  validateSearch: validateBillingSearch,
  component: () => featurePage("Billing", <BillingPage />),
});

// API keys management — long-lived per-user keys for desktop plugins.
const apiKeysRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/api-keys",
  component: () => featurePage("API keys", <ApiKeysPage />),
});

// Public pricing page — sits alongside the landing route so signed-out
// visitors can compare plans before signing up. Existing users get sent
// to /settings/billing?upgrade=true via the page's CTA.
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: () => publicPage(<PricingPage />),
});

// Public legal + contact routes (no auth gate — required for launch +
// Stripe onboarding, and linked from the footer + login consent line).
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: () => publicPage(<TermsPage />),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: () => publicPage(<PrivacyPage />),
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact",
  component: () => publicPage(<ContactPage />),
});

// Admin surface — system-wide tools for support / ops. The layout
// component checks user.is_admin and bounces non-admins back to /chat;
// the server gates the API independently.
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  component: () => featurePage("Admin", <AdminLayout />),
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/users", replace: true });
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/users",
  component: () => featurePage("Admin · Users", <AdminUsersPage />),
});

const adminOrgsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/orgs",
  component: () => featurePage("Admin · Orgs", <AdminOrgsPage />),
});

const adminBillingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/billing",
  component: () => featurePage("Admin · Billing", <AdminBillingPage />),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/audit",
  component: () => featurePage("Admin · Audit", <AdminAuditPage />),
});

// Public invite-acceptance route. Accessible without authentication so
// signed-out users can preview the invitation before being pushed to
// login. The page renders its own sign-in CTA when needed.
const joinInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join/$token",
  component: () => publicPage(<JoinInvitePage />),
});

// Public chat-share route. No auth — anyone with the link reads a
// snapshot of the chat as the owner published it. The page itself
// fetches via `sharesApi.getPublicShare(token)` (no credentials);
// auth state does NOT gate this route.
const sharedChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/$token",
  component: () => publicPage(<SharedChatPage />),
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "*",
  component: () => publicPage(<NotFoundPage />),
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  loginRoute,
  legacyAuthLoginRoute,
  joinInviteRoute,
  sharedChatRoute,
  pricingRoute,
  termsRoute,
  privacyRoute,
  contactRoute,
  appRoute.addChildren([
    chatRoute,
    projectsRoute,
    projectShellRoute.addChildren([
      projectShellIndexRoute,
      projectOverviewRoute,
      projectWorkbenchRoute,
      projectBriefRoute,
      projectSpecsRoute,
      projectRequirementsRoute,
      projectCablesRoute,
      projectBomRoute,
      projectPcbRoute,
      projectSchematicRoute,
      projectConnectorsRoute,
      projectCircuitsRoute,
      projectVersionsRoute,
      projectSyncRoute,
      projectChatRoute,
      projectSnapshotReviewRoute,
      boardShellRoute.addChildren([
        boardShellIndexRoute,
        boardSchematicRoute,
        boardPcbRoute,
        boardBomRoute,
        boardConnectorsRoute,
        boardCircuitsRoute,
      ]),
    ]),
    legacySystemRedirectRoute,
    legacySystemSubRedirectRoute,
    historyRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminOrgsRoute,
      adminBillingRoute,
      adminAuditRoute,
    ]),
    settingsLayoutRoute.addChildren([
      settingsIndexRoute,
      profileRoute,
      organisationsListRoute,
      organisationDetailRoute,
      billingRoute,
      apiKeysRoute,
    ]),
  ]),
  notFoundRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
